import { useCallback, useEffect, useMemo, useState } from "react";
import { Paginator } from "primereact/paginator";
import { Button } from "primereact/button";
import { useAppContext } from "../../providers/ProviderAppContainer";
import SearchBar from "../common/SearchBar";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import HasRequiredAuthority from "../dependencies/HasRequiredAuthority";
import { AUTHORITIES } from "../../constants";
import { TEXT_SMALL } from "../../style";
import TaskCard from "./TaskCard";
import TasksFiltersDrawer from "./TasksFiltersDrawer";
import DialogAddTask from "./DialogAddTask";

export default function Tasks({ scope }) {
    const { requestAPI, showToast } = useAppContext();
    const limit = useMemo(() => 10, []);

    const [loading, setLoading] = useState();
    const [tasks, setTasks] = useState();
    const [filtersDrawerVisibility, setFiltersDrawerVisibility] = useState(false);
    const [searchQuery, setSearchQuery] = useState({ limit, offSet: 0, scope, sort_order: "DESC" });
    const [dialogAddTask, setDialogAddTask] = useState({ visible: false });

    useEffect(() => {
        setSearchQuery({ limit, offSet: 0, scope, sort_order: "DESC" });
    }, [limit, scope]);

    useEffect(() => {
        requestAPI({
            requestMethod: "GET",
            requestPath: "user-tasks",
            requestGetQuery: searchQuery,
            onRequestStart: () => setTasks(),
            setLoading,
            onResponseReceieved: (response, responseCode) => {
                if (response && responseCode === 200) {
                    setTasks(response);
                }
            },
        });
    }, [requestAPI, searchQuery]);

    const countAppliedFilters = useMemo(() => {
        const { search, limit: _l, offSet, scope: _s, sort_order, ...applied } = searchQuery;
        let count = Object.keys(applied).filter((k) => applied[k] != null && applied[k] !== "").length;
        if (sort_order && sort_order !== "DESC") count += 1;
        return count;
    }, [searchQuery]);

    const closeDialogAddTask = useCallback(() => {
        setDialogAddTask((prev) => ({ ...prev, visible: false }));
    }, []);

    const onCreated = useCallback(
        (created) => {
            if (scope === "created" || scope === "all") {
                setTasks((prev) => ({
                    recordsCount: (prev?.recordsCount || 0) + 1,
                    dataSet: [created, ...(prev?.dataSet || [])],
                }));
            }
        },
        [scope],
    );

    const onDelete = useCallback(
        (id) => {
            requestAPI({
                requestPath: `user-tasks/${id}`,
                requestMethod: "DELETE",
                parseResponseBody: false,
                onResponseReceieved: (_, responseCode) => {
                    if (responseCode === 204) {
                        showToast({ severity: "success", summary: "Deleted", detail: "Task Deleted", life: 1000 });
                        setTasks((prev) => ({
                            recordsCount: Math.max((prev?.recordsCount || 1) - 1, 0),
                            dataSet: (prev?.dataSet || []).filter((t) => t.id !== id),
                        }));
                    } else {
                        showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete Task", life: 2000 });
                    }
                },
            });
        },
        [requestAPI, showToast],
    );

    const showAddButton = scope === "created" || scope === "all";

    return (
        <div className="flex flex-column h-full overflow-hidden">
            <div className="flex align-items-center p-2 gap-2 flex-shrink-0">
                <SearchBar
                    className="flex-1"
                    countAppliedFilters={countAppliedFilters}
                    disable={loading}
                    setFiltersDrawerVisibility={setFiltersDrawerVisibility}
                    setSearchQuery={setSearchQuery}
                />
                {showAddButton ? (
                    <HasRequiredAuthority requiredAuthority={AUTHORITIES.CREATE_USER_TASK}>
                        <Button
                            icon="pi pi-plus"
                            severity="warning"
                            onClick={() => setDialogAddTask({ visible: true, closeDialog: closeDialogAddTask, onCreated })}
                        />
                    </HasRequiredAuthority>
                ) : null}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-2 bg-gray-100">
                {loading ? (
                    <Loading />
                ) : !tasks?.dataSet?.length ? (
                    <NoContent error="No Tasks Found" />
                ) : (
                    tasks.dataSet.map((task) => <TaskCard key={task.id} {...task} scope={scope} onDelete={onDelete} />)
                )}
            </div>

            {!loading && tasks?.recordsCount > 0 ? (
                <Paginator
                    first={searchQuery?.offSet}
                    rows={limit}
                    totalRecords={tasks?.recordsCount}
                    onPageChange={(e) => setSearchQuery((prev) => ({ ...prev, offSet: e.first }))}
                    template={{
                        layout: "FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink",
                        CurrentPageReport: (options) => (
                            <span className={`text-center w-8rem ${TEXT_SMALL}`}>
                                {options.first} - {options.last} of {options.totalRecords}
                            </span>
                        ),
                    }}
                />
            ) : null}

            <TasksFiltersDrawer
                visible={filtersDrawerVisibility}
                onHide={() => setFiltersDrawerVisibility(false)}
                setSearchQuery={setSearchQuery}
                showAssigneeFilter={scope === "created" || scope === "all"}
            />

            {dialogAddTask?.visible ? <DialogAddTask {...dialogAddTask} /> : null}
        </div>
    );
}
